/* eslint-disable no-mixed-spaces-and-tabs */
import { call, fork, put, takeEvery, take, select } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { notificationInit } from '../../modules/notifications/actions';
import {
	getModesDictionaryRequest,
	getOrdersGroupsListRequest,
	getOrdersListRequest,
	getOrdersDraftListRequest,
	getStatusesDictionaryRequest,
} from '../../api/requests';
import { REQUEST_ERROR_MESSAGE } from './constants';
import { DISMISS_TIME } from '../../api/constants';
import {
	GET_ORDERS_LIST,
	setIsOrdersListLoadingAction,
	setPaginationAction,
	setOrdersListAction,
	setIsModesLoadingAction,
	setModesAction,
	GET_MODES,
	GET_GROUPS,
	setIsGroupsLoadingAction,
	setGroupsAction,
	setIsStatusesLoadingAction,
	setStatusesAction,
	GET_STATUSES,
	getOrdersListAction,
} from './actions';
import { errorHandler } from '../../api/utils';
import { getIsDraftSelector } from './selectors';
import { getCompanyIdSelector } from '../profile/selectors';
import { createOrdersUpdateChannels } from '../../api/sockets';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { getAddressLink } from '../../utils/getAddressLink';

export function* getOrdersListSaga({ payload: { searchQuery, noLoading } }) {
	try {
		if (!noLoading) {
			yield put(setIsOrdersListLoadingAction(true));
		}

		const isDraft = yield select(getIsDraftSelector());

		const { data, meta, message, toast } = isDraft
			? yield call(getOrdersDraftListRequest, searchQuery)
			: yield call(getOrdersListRequest, searchQuery);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setOrdersListAction(
					data?.map(order => ({
						...order,
						points: order.points.map(point => ({
							...point,
							address: getAddressLink(searchQuery, point.address),
						})),
						link: `?${searchQuery ? `${searchQuery}&` : ''}modal=order&id=${order.id}`,
						group: order.group
							? {
									...order.group,
									link: `?${searchQuery ? `${searchQuery}&` : ''}modal=order-group&id=${
										order.group.id
									}`,
							  }
							: null,
						way: order.way
							? {
									...order.way,
									link: `?${searchQuery ? `${searchQuery}&` : ''}modal=order-way&id=${order.way.id}`,
							  }
							: null,
						req: order.req_id
							? {
									id: order.req_id,
									link: `?${searchQuery ? `${searchQuery}&` : ''}modal=reqs_details&id=${
										order.req_id
									}`,
							  }
							: null,
					})),
				),
			);

			if (meta) {
				yield put(
					setPaginationAction({
						current_page: meta.current_page,
						per_page: meta.per_page,
						from: meta.from,
						last_page: meta.last_page,
						total: meta.total,
					}),
				);
			}
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOrdersListLoadingAction(false));
	}
}

export function* getModesSaga() {
	try {
		yield put(setIsModesLoadingAction(true));

		const { data, message, toast } = yield call(getModesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setModesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsModesLoadingAction(false));
	}
}

export function* getGroupsSaga() {
	try {
		yield put(setIsGroupsLoadingAction(true));

		const { data, message, toast } = yield call(getOrdersGroupsListRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setGroupsAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsGroupsLoadingAction(false));
	}
}

export function* getStatusesSaga() {
	try {
		yield put(setIsStatusesLoadingAction(true));

		const { data, message, toast } = yield call(getStatusesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setStatusesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsStatusesLoadingAction(false));
	}
}

export function* socketOrdersTableUpdateSaga() {
	const companyId = yield select(getCompanyIdSelector());

	const channel = yield call(() => createOrdersUpdateChannels(companyId));

	if (!channel) return;

	while (true) {
		const { companyId } = yield take(channel);

		const query = new URLSearchParams(window.location.search);

		const searchQuery = removeModalQuery(query.toString());

		if (companyId && window.location.pathname.includes('orders')) {
			yield put(getOrdersListAction({ searchQuery, noLoading: true }));
		}
	}
}

export default function* ordersContainerSaga() {
	yield takeEvery(GET_ORDERS_LIST, getOrdersListSaga);
	yield takeEvery(GET_MODES, getModesSaga);
	yield takeEvery(GET_GROUPS, getGroupsSaga);
	yield takeEvery(GET_STATUSES, getStatusesSaga);
}
